import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Action } from 'redux';

import { OrderStatusEnum } from '../../../lib/enums/order-status.enum';
import lang from '../../../lib/language';
import { ReportingCall } from '../../../lib/models/reporting/types';
import { Order } from '../../../lib/models/trading/types';
import { getSummary } from '../../../lib/store/ams/index';
import { getAccountCloseoutStatus } from '../../../lib/store/closeout';
import { getBuyingPowerFromOMS as getBuyingPowerFromOMSCall } from '../../../lib/store/common-actions';
import { getCurrentAccount } from '../../../lib/store/crm/helpers';
import { RootState } from '../../../lib/store/index';
import {
  getAccountTradeHistory,
  getOpenPositions as getPositions,
  setHasOrderUpdate,
} from '../../../lib/store/reporting';
import {
  PENDING_ORDERS_REQUEST_TENNSTATUS,
  TRADE_HISTORY_ORDERS_TENNSTATUSES,
} from '../../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData, OpenPosition } from '../../../lib/store/reporting/types';
import {
  newOrder as serviceNewOrder,
  resetRequestStatus,
} from '../../../lib/store/trading/index';
import MarketStateCache from '../../../lib/store-util/MarketStateCache';
import SymbolStatusCache from '../../../lib/store-util/SymbolStatusCache';
import { isCallStatusReady } from '../../../lib/util/error-handling/StatusByCallHelpers';
import { checkIfSymbolIsHalted } from '../../../lib/util/HaltSymbolHelper';
import { isMarketClosed } from '../../../lib/util/MarketDataHelpers';
import { hasInsufficientPositionQuantity, shouldFetchCloseoutStatus } from '../../../lib/util/trading-position-helpers';
import { isMessageSummaryAffectingStatus } from '../../../lib/util/trading-status-helpers';
import { orderStatusToCallStatus } from '../../../lib/util/TradingHelpers';

import { Preloader, ProcessFinishedModal } from '../../index';
import {
  buildOrderResultMessages,
} from '../OrderHelpers';
import useNewOrderTimer from '../useNewOrderTimer';
import useRefreshBuyingPower from '../useRefreshBuyingPower';

import CreateTicketOrder from './CreateTicketOrder';

type OwnProps = {
    symbol: string;
}
type Props = OwnProps & ReduxProps

const OrderNavigation = (props: Props) => {
  const {
    symbol,
    positions,
    requestStatus,
    submittedOrder,
    accountReferenceId,
    accountOpenPositionsCompleted,
    newOrder,
    getOpenPositions,
    getOrders,
    myStocksUpdate,
    resetStatus,
    getMessageSummary,
    getBuyingPowerFromOMS,
    getCloseoutStatus,
  } = props;

  const [order, setOrder] = useState<Order>({});
  const [updateOrder, setUpdateOrder] = useState<boolean>(false);
  const [fromReview, setFromReview] = useState<boolean>(false);
  const [notEnoughStocks, setNotEnoughStocks] = useState<boolean>(false);
  const [showFinishModal, setShowFinishModal] = useState<boolean>(false);
  const [orderInitialized, setOrderInitialized] = useState<boolean>(false);
  const [positionsQuantity, setPositionsQuantity] = useState<number | null>(null);

  const { marketState } = MarketStateCache.use();
  const symbolsStatusCache = SymbolStatusCache.use(symbol);
  const marketClosed = isMarketClosed(marketState);
  const { ordType, orderQty, side, status } = submittedOrder;
  const {
    orderTimeoutPassed, showFillOrder, showExpired, hasNoResponse,
  } = useNewOrderTimer(showFinishModal, ordType, status, requestStatus);
  const rejectedOrder = requestStatus === OrderStatusEnum.Rejected;
  const orderInProgress = requestStatus === OrderStatusEnum.InProgress;
  const showOrderModal = (orderTimeoutPassed || hasNoResponse || rejectedOrder) && showFinishModal;

  useEffect(() => {
    setOrderInitialized(false);
    if (orderInProgress) {
      setOrderInitialized(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  useRefreshBuyingPower(showOrderModal, getBuyingPowerFromOMS);

  const managePositions = (isFromReview: boolean) => {
    const position = positions.find((item: OpenPosition) => item.symbol === symbol);

    if (isFromReview) {
      if (hasInsufficientPositionQuantity(position, order)) {
        setNotEnoughStocks(true);
      } else {
        newOrder(order);
        setShowFinishModal(true);
      }
      setFromReview(false);
    }
    setPositionsQuantity(position ? Math.abs(position.quantity) : 0);
    setUpdateOrder(false);
    setOrderInitialized(true);
  };

  useEffect(() => {
    if (showFinishModal) {
      setFromReview(false);
    }
  }, [showFinishModal]);

  useEffect(() => {
    if (orderInProgress) {
      return;
    }
    if (accountOpenPositionsCompleted) {
      managePositions(fromReview);
    } else if (updateOrder) {
      setOrderInitialized(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountOpenPositionsCompleted, fromReview, positions, requestStatus]);

  if (!orderInitialized) {
    return <Preloader style={{ height: '465px' }} text="" />;
  }

  const onCloseResultOrderModal = () => {
    setShowFinishModal(false);
    if (orderInProgress) {
      resetStatus();
    }
    if (!marketClosed) {
      setUpdateOrder(true);
      getOpenPositions(symbol);
      myStocksUpdate(false);

      if (shouldFetchCloseoutStatus(positions, { symbol, side, orderQty, status }, accountReferenceId)) {
        getCloseoutStatus(accountReferenceId);
      }
    }
    if (isMessageSummaryAffectingStatus(status)) {
      getMessageSummary();
    }
    // TODO: Check if these calls can be made as one and populate trade history and pending orders together
    const commonData = { symbols_StringList: symbol, isSymbolDetails: true };
    getOrders({ ...commonData, tenNStatuses_StringList: TRADE_HISTORY_ORDERS_TENNSTATUSES });
    getOrders({ ...commonData, tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS, isPendingOrdersCall: true });
  };

  return (
    <>
      <CreateTicketOrder
        symbol={symbol}
        isSymbolHalted={checkIfSymbolIsHalted(symbolsStatusCache)}
        setFromReview={(value) => setFromReview(value)}
        positionsQuantity={positionsQuantity || null}
        setOrderInfo={(value) => setOrder(value)}
      />
      {notEnoughStocks && (
      <ProcessFinishedModal
        symbol={symbol}
        processSuccessMessageTitle=""
        callStatus={orderStatusToCallStatus(OrderStatusEnum.Rejected)}
        processFailedMessage={lang.webTradingIncorrectStocks()}
        toggleModal={() => {
          if (!marketClosed) {
            setUpdateOrder(true);
          }
          setShowFinishModal(false);
          setNotEnoughStocks(false);
        }}
      />
      )}
      {showOrderModal && (
      <ProcessFinishedModal
        symbol={symbol}
        {...buildOrderResultMessages(symbol, submittedOrder, requestStatus, { showExpired, showFillOrder })}
        toggleModal={onCloseResultOrderModal}
      />
      )}
    </>
  );
};

const mapStateProps = (state: RootState) => ({
  submittedOrder: state.trading.order,
  positions: state.reporting.openPositions,
  requestStatus: state.trading.requestStatus,
  accountReferenceId: getCurrentAccount(state),
  accountOpenPositionsCompleted: isCallStatusReady(ReportingCall.getOpenPositions, state.reporting.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  resetStatus: () => dispatch(resetRequestStatus()),
  newOrder: (order: Order) => dispatch(serviceNewOrder({ order })),
  getOrders: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getOpenPositions: (symbol?: string) => dispatch(getPositions({ symbol })),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
  getMessageSummary: () => dispatch(getSummary()),
  getBuyingPowerFromOMS: () => dispatch(getBuyingPowerFromOMSCall()),
  getCloseoutStatus: (accountRefId: string) => dispatch(getAccountCloseoutStatus(accountRefId)),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(OrderNavigation);
